import React, { useState, useEffect } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Gift, Sparkles, Clock, Heart, CheckCircle2, Users, X, Share2, ShieldCheck, Award, MessageSquare } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { getPricesBySqft } from "@/lib/pricing";

export const GiftCardWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [isGroupGift, setIsGroupGift] = useState(false);
  const [personalMessage, setPersonalMessage] = useState("");
  const [cardTheme, setCardTheme] = useState("classic");
  
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setProgress(10);

      // Check for pre-filled plan from custom builder
      const params = new URLSearchParams(window.location.search);
      if (params.get('gift') === 'true') {
        const type = params.get('type') || 'standard';
        const size = parseInt(params.get('size') || '1500');
        const prices = getPricesBySqft(size);
        const basePrice = prices[type as keyof typeof prices] || 199;
        setSelectedAmount(basePrice);
        setCardTheme('new-home');
        setPersonalMessage(`Enjoy your ${type.replace('-', ' ')} cleaning!`);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLoading && isOpen) {
      interval = setInterval(() => {
        setProgress((prev) => (prev < 90 ? prev + 10 : prev));
      }, 400);
      
      // Track abandoned gift card
      fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
        method: "POST",
        headers: { "Content-Type": "application/json", "version": "2021-07-28" },
        body: JSON.stringify({
          type: "external_page_view",
          timestamp: Date.now(),
          title: "Abandoned Gift Card Intent",
          url: window.location.href,
          trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
          locationId: "M7JmlFFXdtFNLwyfY61D",
          sessionId: crypto.randomUUID(),
        })
      }).catch(() => {});
    } else if (!isLoading) {
      setProgress(100);
    }
    return () => clearInterval(interval);
  }, [isLoading, isOpen]);

  useEffect(() => {
    if (isOpen && !document.querySelector('script[src="https://uplevelcleaning.bookingkoala.com/resources/embed.js"]')) {
      const script = document.createElement("script");
      script.src = "https://uplevelcleaning.bookingkoala.com/resources/embed.js";
      script.defer = true;
      document.body.appendChild(script);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a');
      const href = target?.getAttribute('href');
      
      // Handle both internal route and direct booking koala link
      if (href === '/gift-cards' || href?.includes('gift-cards/send')) {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    const handleCustomEvent = () => setIsOpen(true);

    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('open-gift-cards', handleCustomEvent);
    
    return () => {
      document.removeEventListener('click', handleGlobalClick);
      document.removeEventListener('open-gift-cards', handleCustomEvent);
    };
  }, []);

  const handleShare = async () => {
    const shareData = {
      title: 'Give the Gift of a Clean Home',
      text: 'Purchase an Uplevel Cleaning gift card for your friends and family.',
      url: window.location.origin + "/gift-cards"
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      toast({
        title: "Link copied!",
        description: shareData.url,
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-md md:max-w-3xl lg:max-w-5xl p-0 overflow-y-auto bg-background border-l border-border"
      >
        <div className="sticky top-0 z-20 bg-background border-b border-border shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Gift className="h-5 w-5 text-primary" />
                Purchase Gift Card
              </h2>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 hidden sm:flex">
                <div className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-primary" /> Secure Checkout</div>
                <div className="flex items-center gap-1"><Award className="h-3 w-3 text-primary" /> Instant Delivery</div>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 pr-4 sm:pr-8">
              <Button variant="ghost" size="sm" onClick={() => document.dispatchEvent(new CustomEvent('open-chat'))} className="gap-2 h-8 text-xs hidden md:flex">
                <MessageSquare className="h-3.5 w-3.5" />
                <span>Chat</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={handleShare} className="gap-2 h-8 text-xs">
                <Share2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Share</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="w-full min-h-screen bg-muted/20 relative">
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center pt-32 px-8 bg-background z-10">
              <div className="w-full max-w-md space-y-4">
                <div className="flex justify-between text-sm font-medium text-muted-foreground">
                  <span>Loading gift cards...</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-2 w-full" />
              </div>
            </div>
          )}
          
          <div className={`p-4 md:p-8 transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-3">Give the Gift of Free Time</h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">Perfect for new parents, busy professionals, or anyone who deserves a spotless home.</p>
            </div>
            
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Left Column: Visuals & Benefits */}
              <div className="lg:col-span-5 space-y-6">
                {/* Interactive Gift Card Preview */}
                <div className={`relative aspect-[1.6/1] rounded-2xl overflow-hidden shadow-2xl group transition-transform duration-500 hover:scale-[1.02] ${
                  cardTheme === 'classic' ? 'from-primary via-primary/80 to-accent' :
                  cardTheme === 'birthday' ? 'from-pink-500 via-purple-500 to-indigo-500' :
                  cardTheme === 'holiday' ? 'from-red-600 via-green-600 to-red-800' :
                  'from-amber-400 via-orange-500 to-red-500' // new home
                }`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${
                    cardTheme === 'classic' ? 'from-primary via-primary/80 to-accent' :
                    cardTheme === 'birthday' ? 'from-pink-500 via-purple-500 to-indigo-500' :
                    cardTheme === 'holiday' ? 'from-red-600 via-green-600 to-red-800' :
                    'from-amber-400 via-orange-500 to-red-500'
                  }`}></div>
                  <div className="absolute inset-0 opacity-20 bg-[url('https://vibe.filesafe.space/1776798830316075366/assets/8df8ee03-c93d-4bf0-a6e9-1c7f843f7ab6.jpg')] bg-cover bg-center mix-blend-overlay"></div>
                  <div className="relative h-full p-6 flex flex-col justify-between text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold tracking-tight">Uplevel Cleaning</h3>
                        <p className="text-white/80 font-medium text-sm">
                          {cardTheme === 'birthday' ? 'Happy Birthday!' : 
                           cardTheme === 'holiday' ? 'Happy Holidays!' : 
                           cardTheme === 'new-home' ? 'Happy New Home!' : 'Gift Card'}
                        </p>
                      </div>
                      <Gift className="w-6 h-6 text-white/90" />
                    </div>
                    
                    {personalMessage && (
                      <div className="my-auto text-center italic text-white/90 text-sm px-4 line-clamp-2">
                        "{personalMessage}"
                      </div>
                    )}

                    <div className="flex justify-between items-end">
                      <div className="space-y-1">
                        <p className="text-xs text-white/70 uppercase tracking-wider">Value</p>
                        <p className="text-3xl font-bold">${selectedAmount || "199"}</p>
                      </div>
                      <Sparkles className="w-5 h-5 text-white/50" />
                    </div>
                  </div>
                </div>

                {/* Card Customization */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">Select Theme</label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { id: 'classic', label: 'Classic' },
                        { id: 'birthday', label: 'Birthday' },
                        { id: 'holiday', label: 'Holiday' },
                        { id: 'new-home', label: 'New Home' }
                      ].map(theme => (
                        <button
                          key={theme.id}
                          onClick={() => setCardTheme(theme.id)}
                          className={`py-1.5 text-xs rounded-lg border-2 font-bold transition-all ${cardTheme === theme.id ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-foreground hover:border-primary/50'}`}
                        >
                          {theme.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">Personal Message (Preview)</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Enjoy your clean home!" 
                      value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                      maxLength={60}
                    />
                  </div>
                </div>

                {/* Quick Select Amounts */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Popular Amounts</p>
                    <div className="flex items-center gap-2">
                      <Switch id="group-gift" checked={isGroupGift} onCheckedChange={setIsGroupGift} />
                      <label htmlFor="group-gift" className="text-sm font-medium flex items-center gap-1 cursor-pointer">
                        <Users className="w-4 h-4 text-primary" /> Group Gift
                      </label>
                    </div>
                  </div>
                  
                  {isGroupGift && (
                    <div className="bg-primary/10 p-4 rounded-xl border border-primary/20 text-sm mb-4 animate-in fade-in slide-in-from-top-2">
                      <p className="font-semibold text-primary mb-1">Split the cost with friends!</p>
                      <p className="text-muted-foreground">Select an amount, and we will generate a shareable link where everyone can chip in towards the total.</p>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    {[199, 299, 499].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setSelectedAmount(amount)}
                        className={`py-2.5 rounded-xl border-2 font-bold transition-all ${selectedAmount === amount ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-foreground hover:border-primary/50'}`}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Select an amount above to preview. Enter the actual amount in the form.</p>
                </div>

                {/* Benefits */}
                <div className="bg-card rounded-2xl p-6 border shadow-sm space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold">Why give a clean home?</h3>
                    <button onClick={() => { setIsOpen(false); navigate('/gift-card-balance'); }} className="text-sm font-bold text-primary hover:underline">
                      Check Balance →
                    </button>
                  </div>
                  <ul className="space-y-3">
                    {[
                      { icon: Clock, text: "Gives them hours of free time back" },
                      { icon: Heart, text: "Perfect for new parents or newlyweds" },
                      { icon: Sparkles, text: "Reduces stress and mental load" },
                      { icon: CheckCircle2, text: "Never expires, use anytime" }
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <div className="p-1.5 bg-primary/10 rounded-full text-primary">
                          <item.icon className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-sm">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Column: BookingKoala Form */}
              <div className="lg:col-span-7">
                <div className="bg-background p-2 md:p-4 rounded-2xl shadow-xl border border-border/50">
                  <iframe 
                    src="https://uplevelcleaning.bookingkoala.com/gift-cards/send?embed=true" 
                    style={{ border: "none", height: "1000px", width: "100%" }} 
                    scrolling="no"
                    title="Gift Card Purchase"
                    onLoad={() => setIsLoading(false)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
